import { z } from 'zod';

/**
 * Static OIDC client configuration. This is what the consumer (web/mobile
 * app) hard-codes or pulls from its build config.
 */
export interface OidcClientConfig {
  /** Issuer URL, e.g. https://kc.example/realms/mrrmlab */
  issuer: string;
  clientId: string;
  redirectUri: string;
  /** Defaults to ['openid', 'profile', 'email']. */
  scopes?: string[];
  /** Required `aud` claim on the access token, if any. */
  audience?: string;
  /** Optional post-logout redirect (RP-Initiated Logout). */
  postLogoutRedirectUri?: string;
}

/** Endpoints discovered from /.well-known/openid-configuration. */
export const oidcDiscoverySchema = z.object({
  issuer: z.string().url(),
  authorization_endpoint: z.string().url(),
  token_endpoint: z.string().url(),
  jwks_uri: z.string().url(),
  end_session_endpoint: z.string().url().optional(),
  userinfo_endpoint: z.string().url().optional(),
});
export type OidcDiscovery = z.infer<typeof oidcDiscoverySchema>;

/** Token endpoint response. */
export const tokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number().int().positive().optional(),
  refresh_token: z.string().optional(),
  id_token: z.string().optional(),
  scope: z.string().optional(),
});
export type TokenResponse = z.infer<typeof tokenResponseSchema>;

/** What we persist in the token store between sessions. */
export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  /** Absolute expiry (ms since epoch) of the access token. */
  expiresAt?: number;
  scope?: string;
}

/** State carried between `startLogin` and `handleCallback` (per OAuth2 + PKCE). */
export interface PendingLogin {
  state: string;
  nonce: string;
  codeVerifier: string;
  /** Where to send the user after a successful login. */
  returnTo?: string;
}

export interface AuthUser {
  sub: string;
  preferredUsername?: string;
  email?: string;
  name?: string;
  realmRoles: string[];
  /** Raw decoded ID token claims. */
  claims: Record<string, unknown>;
}
